import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, useLang, useT, useTheme, API } from '../../App';
import { navItemKeys, isNavVisible } from '../../lib/navItems';
import { loc } from '../../lib/localize';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from '../ui/command';
import { Settings, User, LogOut, Sun, Moon, Languages, Briefcase } from 'lucide-react';

export default function CommandPalette() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { lang, toggleLang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState(null);
  const fr = lang === 'fr';

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); }
    };
    const onEvt = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('gcfe:cmdk', onEvt);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('gcfe:cmdk', onEvt); };
  }, []);

  useEffect(() => {
    if (!open || projects) return;
    axios.get(`${API}/projects`)
      .then((r) => setProjects(Array.isArray(r.data) ? r.data : r.data.projects || []))
      .catch(() => setProjects([]));
  }, [open, projects]);

  const run = useCallback((fn) => { setOpen(false); fn(); }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="gcfe-cmdk p-0 max-w-xl overflow-hidden bg-[#0C1E27] border-[#1B3945] [&>button]:hidden" data-testid="command-palette">
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <Command className="bg-transparent">
          <CommandInput
            placeholder={fr ? 'Commande, page ou projet…  (ex: PIPE, DASH, Sénégal)' : 'Command, page or project…  (e.g. PIPE, DASH, Kenya)'}
            data-testid="command-palette-input"
          />
          <CommandList className="max-h-[420px]">
            <CommandEmpty className="py-6 text-center text-xs font-mono text-slate-500">
              {fr ? 'AUCUN RÉSULTAT' : 'NO RESULTS'}
            </CommandEmpty>

            <CommandGroup heading={fr ? 'Navigation' : 'Navigation'}>
              {navItemKeys.filter((i) => isNavVisible(i, user)).map((item) => (
                <CommandItem key={item.to} value={`${item.code} ${t(item.key)}`} onSelect={() => run(() => navigate(item.to))} data-testid={`cmdk-nav-${item.testId}`}>
                  <item.icon className="w-4 h-4 mr-2 text-slate-400" />
                  <span>{t(item.key)}</span>
                  <CommandShortcut className="font-mono text-[#F5C544]">{item.code}</CommandShortcut>
                </CommandItem>
              ))}
              {user?.role === 'admin' && (
                <CommandItem value={`ADM ${t('nav_admin')}`} onSelect={() => run(() => navigate('/admin'))} data-testid="cmdk-nav-admin">
                  <Settings className="w-4 h-4 mr-2 text-slate-400" />
                  <span>{t('nav_admin')}</span>
                  <CommandShortcut className="font-mono text-[#F5C544]">ADM</CommandShortcut>
                </CommandItem>
              )}
              <CommandItem value={`PROF ${t('nav_profile')}`} onSelect={() => run(() => navigate('/profile'))} data-testid="cmdk-nav-profile">
                <User className="w-4 h-4 mr-2 text-slate-400" />
                <span>{t('nav_profile')}</span>
                <CommandShortcut className="font-mono text-[#F5C544]">PROF</CommandShortcut>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator className="bg-[#1B3945]" />

            <CommandGroup heading={fr ? 'Projets' : 'Projects'}>
              {(projects || []).slice(0, 40).map((p) => (
                <CommandItem
                  key={p.project_id}
                  value={`${loc(p, 'title', lang)} ${p.country} ${p.sector} ${p.project_id}`}
                  onSelect={() => run(() => navigate(`/projects/${p.project_id}`))}
                  data-testid={`cmdk-project-${p.project_id}`}
                >
                  <Briefcase className="w-4 h-4 mr-2 text-[#45B08F]" />
                  <span className="truncate">{loc(p, 'title', lang)}</span>
                  <span className="ml-2 text-[10px] font-mono text-slate-500 uppercase">{p.country} · {p.sector}</span>
                  {p.readiness_grade && <CommandShortcut className="font-mono text-[#45B08F]">{p.readiness_grade}</CommandShortcut>}
                </CommandItem>
              ))}
            </CommandGroup>

            <CommandSeparator className="bg-[#1B3945]" />

            <CommandGroup heading={fr ? 'Actions' : 'Actions'}>
              <CommandItem value={`LANG ${fr ? 'Langue FR/EN' : 'Language FR/EN'}`} onSelect={() => run(toggleLang)} data-testid="cmdk-action-lang">
                <Languages className="w-4 h-4 mr-2 text-slate-400" />
                <span>{fr ? 'Langue FR / EN' : 'Language FR / EN'}</span>
                <CommandShortcut className="font-mono text-[#F5C544]">LANG</CommandShortcut>
              </CommandItem>
              <CommandItem value={`EXIT ${fr ? 'Déconnexion' : 'Logout'}`} onSelect={() => run(async () => { await logout(); navigate('/'); })} data-testid="cmdk-action-logout">
                <LogOut className="w-4 h-4 mr-2 text-[#EC4868]" />
                <span>{fr ? 'Déconnexion' : 'Logout'}</span>
                <CommandShortcut className="font-mono text-[#F5C544]">EXIT</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          </CommandList>
          <div className="flex items-center justify-between px-3 h-7 border-t border-[#1B3945] text-[10px] font-mono text-slate-500">
            <span>↑↓ {fr ? 'naviguer' : 'navigate'} · ↵ {fr ? 'ouvrir' : 'open'} · esc {fr ? 'fermer' : 'close'}</span>
            <span className="text-[#F5C544]">GCFE TERMINAL</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
